import { parseAmount } from './amount.js';
import { resolvePledgeRow } from '../../pledge-pay/pay-pledge-map.js';
import { pledgesConfig } from '../pledges/config.js';
import { readPledgeActionDataFromRow } from './row-data.js';
import {
    isKioskDownloadsDisabled,
    kioskDownloadsDisabledMessage,
} from '../../shared/kiosk-downloads.js';

function isManagerKioskNoPrint() {
    return typeof window !== 'undefined' && window.managerKioskNoPrint === true;
}

const DISABLED_TOOLTIPS = {
    sendStatement: 'Statement cannot be sent for members with a $0 balance.',
    sendStatementNoContact: 'This member has no email or phone number on file.',
    sendStatementNoPledges: 'No open pledges to send.',
    printStatementNoPledges: 'No pledges to print.',
    viewMember: 'Pledge is not associated with a member.',
    viewAllPledges: 'Pledge is not associated with a member.',
    alreadyViewingMemberPledges: 'Already viewing all pledges for this member.',
    viewPayments: 'No payments recorded yet.',
    archivedMember: 'Not available for archived members.',
};

function lockArchivedMemberDescriptor(descriptor) {
    return {
        ...descriptor,
        disabled: true,
        tooltip: DISABLED_TOOLTIPS.archivedMember,
        onClick: null,
    };
}

function memberHasEmailOrPhone(data) {
    const email = String(data.email || '').trim();
    const phone = String(data.phone || '').trim();
    return email !== '' || phone !== '';
}

function isFilteredToMember(memberId) {
    const filteredMemberId = pledgesConfig.memberId;
    if (filteredMemberId == null || filteredMemberId === '') {
        return false;
    }
    if (memberId == null || memberId === '') {
        return false;
    }
    return String(filteredMemberId) === String(memberId);
}

const MOBILE_ACTION_BTN_CLASS = 'stack-btn press-feedback';

const MOBILE_ACTION_UX_ORDER = [
    'open-pay-bill-modal:pay',
    'open-pay-bill-modal:mark',
    'view-recur-payments',
    'open-edit-modal',
    'edit-ongoing',
    'cancel-recurring',
    'edit-paid-donation',
    'apply-donation-to-pledge',
    'open-invoice-modal',
    'open-print-statement-modal',
    'view-sent-statements',
    'resend-receipt',
    'open-associate-modal:associate',
    'open-associate-modal:re-associate',
    'view-all-member-pledges',
    'open-delete-modal',
    'open-delete-modal:paid',
    'view-profile',
    'view-member',
    'edit-member',
    'view-all-pledges',
    'send-statement',
    'print-statement',
    'view-mi-sheberach-card',
    'open-mi-sheberach-card',
    'download-mi-sheberach-card',
    'view-relationships',
];

export default {
    DISABLED_TOOLTIPS,
    canShowWriteAction(capability) {
        return this.canEditCapability(capability);
    },

    resolvePledgeForActions(pledgeId, tableName, $row) {
        if (pledgeId === undefined || pledgeId === null || pledgeId === '') {
            throw new Error('resolvePledgeForActions: pledgeId is required');
        }
        if (!tableName) {
            throw new Error('resolvePledgeForActions: tableName is required');
        }
        const rowData = $row && $row.length ? readPledgeActionDataFromRow($row) : null;
        const mapPledge = resolvePledgeRow(tableName, pledgeId);
        if (mapPledge && rowData) {
            return { ...rowData, ...mapPledge };
        }
        if (mapPledge) {
            return mapPledge;
        }
        if (rowData) {
            return rowData;
        }
        return null;
    },

    getMemberDataFromRow($row) {
        return {
            member_id: $row.data('member-id'),
            first_name: $row.data('first-name') || '',
            last_name: $row.data('last-name') || '',
            phone: $row.data('phone') || '',
            email: $row.data('email') || '',
            pledge_ids: $row.data('pledge-ids') || '',
            pledge_total: parseAmount($row.data('pledge-total')),
            member_confirmed: $row.data('member-confirmed') === 1,
            congregation_archive: $row.data('congregation-archive') === 1,
            is_member: $row.data('is-member') === 1,
            manager_congregation: $row.data('manager-congregation') || '',
            has_relationships: $row.data('has-relationships') === 1 || $row.data('has-relationships') === true,
        };
    },

    callCallback(functionName, ...args) {
        const fn = this.callbacks[functionName];
        if (typeof fn !== 'function') {
            throw new Error('RowExpander: ' + functionName + ' is required');
        }
        return fn(...args);
    },

    getActionButtonClass(type) {
        return `btn btn-sm ${type === 'danger' ? 'btn-outline-danger' : 'btn-outline-primary'} action-btn btn-shine`;
    },

    rowActionAttrName() {
        return 'data-pledges-row-action';
    },

    makeActionDescriptor(text, type, options = {}) {
        return {
            text,
            type: type || 'primary',
            action: options.action || null,
            expandAction: options.expandAction || null,
            attrs: options.attrs || {},
            onClick: options.onClick || null,
            icon: options.icon || null,
            group: options.group || 'general',
            extraClass: options.extraClass || '',
            writeOption: !!options.writeOption,
            disabled: !!options.disabled,
            tooltip: options.tooltip,
            profileCta: !!options.profileCta,
            memberId: options.memberId,
            accent: !!options.accent,
        };
    },

    renderDesktopActionButton(descriptor) {
        if (descriptor.profileCta) {
            return this.createMemberProfileCta(descriptor.memberId, {
                disabled: descriptor.disabled,
                tooltip: descriptor.tooltip,
            });
        }

        const self = this;
        const $btn = this.buildActionButtonEl(
            this.getActionButtonClass(descriptor.type),
            descriptor.text,
            null,
            descriptor.group,
            descriptor.extraClass
        );

        if (descriptor.action) {
            $btn.attr(this.rowActionAttrName(), descriptor.action);
        }
        Object.entries(descriptor.attrs || {}).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                $btn.attr(key, value);
            }
        });
        if (descriptor.writeOption) {
            $btn.attr('data-manager-write-option', '1');
        }
        if (descriptor.disabled) {
            $btn.attr('disabled', true).addClass('disabled');
            if (descriptor.tooltip) {
                return this.wrapDisabledButtonWithTooltip($btn, descriptor.tooltip, descriptor.group);
            }
            return $btn;
        }
        if (descriptor.tooltip) {
            $btn.attr('title', descriptor.tooltip);
        }
        if (descriptor.onClick) {
            $btn.on('click', function (e) {
                e.preventDefault();
                e.stopPropagation();
                if ($(this).hasClass('action-btn-loading')) {
                    return;
                }
                self.setActionButtonLoading($(this), descriptor.onClick);
            });
        }
        return $btn;
    },

    renderMobileActionButton(descriptor) {
        if (descriptor.profileCta) {
            return this.renderMobileActionButton({
                text: 'Full profile',
                type: 'primary',
                expandAction: descriptor.disabled ? null : 'view-profile',
                attrs: { 'data-member-id': descriptor.memberId },
                group: descriptor.group || 'status',
                icon: 'fa-solid fa-arrow-right',
                accent: true,
                disabled: descriptor.disabled,
                tooltip: descriptor.tooltip,
            });
        }

        let className = MOBILE_ACTION_BTN_CLASS;
        if (descriptor.type === 'danger') {
            className += ' stack-btn-danger';
        }
        if (descriptor.accent) {
            className += ' stack-btn-accent';
        }

        const $btn = $('<button>').attr('type', 'button').addClass(className);
        if (descriptor.group) {
            $btn.attr('data-action-group', descriptor.group);
        }
        if (descriptor.action) {
            $btn.attr(this.rowActionAttrName(), descriptor.action);
        }
        if (descriptor.expandAction) {
            $btn.attr('data-action', descriptor.expandAction);
        }
        Object.entries(descriptor.attrs || {}).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                $btn.attr(key, value);
            }
        });
        if (descriptor.writeOption) {
            $btn.attr('data-manager-write-option', '1');
        }
        if (descriptor.disabled) {
            $btn.attr('disabled', true).addClass('disabled');
            if (descriptor.tooltip) {
                $btn.attr('title', descriptor.tooltip);
            }
        } else if (descriptor.tooltip) {
            $btn.attr('title', descriptor.tooltip);
        }

        const $iconWrap = $('<span>').addClass('stack-btn__icon').attr('aria-hidden', 'true');
        if (descriptor.icon) {
            $iconWrap.append($('<i>').addClass(descriptor.icon));
        }
        $btn.append($iconWrap);
        $btn.append($('<span>').addClass('stack-btn__label').text(descriptor.text));
        return $btn;
    },

    mobileActionSortKey(descriptor) {
        const action = descriptor.action || descriptor.expandAction || '';
        if (action === 'open-pay-bill-modal') {
            const mode = descriptor.attrs && descriptor.attrs['data-pay-mode'];
            return mode ? `open-pay-bill-modal:${mode}` : action;
        }
        if (action === 'open-associate-modal') {
            const mode = descriptor.attrs && descriptor.attrs['data-associate-mode'];
            return mode ? `open-associate-modal:${mode}` : action;
        }
        if (action === 'open-delete-modal' && descriptor.attrs && descriptor.attrs['data-delete-paid']) {
            return 'open-delete-modal:paid';
        }
        if (descriptor.profileCta) {
            return 'view-profile';
        }
        return action;
    },

    sortMobileActionDescriptors(descriptors) {
        return descriptors.slice().sort((left, right) => {
            const leftKey = this.mobileActionSortKey(left);
            const rightKey = this.mobileActionSortKey(right);
            const leftIndex = MOBILE_ACTION_UX_ORDER.indexOf(leftKey);
            const rightIndex = MOBILE_ACTION_UX_ORDER.indexOf(rightKey);
            const leftRank = leftIndex === -1 ? MOBILE_ACTION_UX_ORDER.length : leftIndex;
            const rightRank = rightIndex === -1 ? MOBILE_ACTION_UX_ORDER.length : rightIndex;
            if (leftRank !== rightRank) {
                return leftRank - rightRank;
            }
            return String(left.text || '').localeCompare(String(right.text || ''));
        });
    },

    fillMobileActionsPanel($panel, descriptors) {
        $panel.empty();
        this.renderActionButtons(
            this.sortMobileActionDescriptors(descriptors),
            this.renderMobileActionButton
        ).forEach(($btn) => {
            $panel.append($btn);
        });
    },

    renderActionButtons(descriptors, renderer) {
        return descriptors.map((descriptor) => renderer.call(this, descriptor));
    },

    pledgeRowAttrs(pledgeId, data, extra = {}) {
        return {
            'data-pledge-id': pledgeId,
            'data-table-name': data.table_name || 'pledge',
            'data-pledge-type': data.type || '',
            ...extra,
        };
    },

    buildActionButtonEl(btnClass, text, iconClass, group = 'general', extraClass = '') {
        const $content = $('<span>').addClass('action-btn-content');
        if (iconClass) $content.append($('<i>').addClass(iconClass), ' ', text);
        else $content.text(text);
        const className = extraClass ? `${btnClass} ${extraClass}` : btnClass;
        return $('<button>').attr('type', 'button').attr('data-action-group', group).addClass(className).append($content);
    },

    wrapDisabledButtonWithTooltip($btn, tooltip, group = 'general') {
        $btn.addClass('action-btn-disabled-tooltip');
        return $('<span>').addClass('d-inline-block').attr({
            title: tooltip,
            'data-action-group': group,
            'data-toggle': 'tooltip',
            'data-placement': 'top',
        }).append($btn);
    },

    createMemberProfileCta(memberId, options = {}) {
        const disabled = !!options.disabled;
        const tooltip = options.tooltip;
        const $arrow = $('<span>')
            .addClass('accent-arrow-pill__arrow')
            .attr('aria-hidden', 'true')
            .append($('<i>').addClass('fa-solid fa-arrow-right'));
        const $cta = $('<a>')
            .attr({
                href: '#',
                'data-action-group': 'status',
            })
            .addClass('accent-arrow-pill')
            .append($('<span>').addClass('accent-arrow-pill__label').text('Full profile'))
            .append($arrow);

        if (disabled) {
            $cta.addClass('disabled').attr({
                'aria-disabled': 'true',
                tabindex: '-1',
            });
            if (tooltip) {
                return this.wrapDisabledButtonWithTooltip($cta, tooltip, 'status');
            }
            return $cta;
        }

        return $cta.on('click', (e) => {
            e.preventDefault();
            this.callCallback('openMemberProfilePage', memberId);
        });
    },

    buildActionButtonDescriptors(pledgeId, data) {
        const descriptors = [];
        if (this.portal === 'member') {
            this.composeMemberPortalPledgeDescriptors(descriptors, pledgeId, data);
            this.pushActivateRecurringDescriptor(descriptors, pledgeId, data);
            return descriptors;
        }
        if (this.isEditableFailedRecurring(data)) {
            this.addRecurringPledgeDescriptors(descriptors, pledgeId, data);
        } else if (data.paid) {
            this.addPaidPledgeDescriptors(descriptors, pledgeId, data);
        } else if (data.recurring && !data.awaiting_activation) {
            this.addRecurringPledgeDescriptors(descriptors, pledgeId, data);
        } else {
            this.addUnpaidPledgeDescriptors(descriptors, pledgeId, data);
        }
        this.pushActivateRecurringDescriptor(descriptors, pledgeId, data);
        this.addCommonDescriptors(descriptors, pledgeId, data);
        return descriptors;
    },

    buildActionButtons(pledgeId, data) {
        return this.renderActionButtons(
            this.buildActionButtonDescriptors(pledgeId, data),
            this.renderDesktopActionButton
        );
    },

    composeMemberPortalPledgeDescriptors(descriptors, pledgeId, data) {
        if (data.paid && !this.isEditableFailedRecurring(data)) {
            if (this.canShowPayInstallments(data)) {
                this.pushPayInstallmentsDescriptor(descriptors, pledgeId, data);
            }
            this.pushViewPaymentsDescriptor(descriptors, pledgeId, data);
            this.addResendReceiptDescriptor(descriptors, pledgeId, data);
            return;
        }
        if (data.awaiting_activation) {
            this.pushPayBillDescriptor(descriptors, pledgeId, data);
            return;
        }
        if (data.type === 'Donation') {
            this.pushEditRecurringDescriptor(descriptors, pledgeId, data);
            if (this.canShowPayInstallments(data)) {
                this.pushPayInstallmentsDescriptor(descriptors, pledgeId, data);
            }
            this.pushViewPaymentsDescriptor(descriptors, pledgeId, data);
            this.addResendReceiptDescriptor(descriptors, pledgeId, data);
            return;
        }
        if (data.active_ongoing || this.isEditableFailedRecurring(data)) {
            this.pushEditRecurringDescriptor(descriptors, pledgeId, data);
            if (this.canShowPayInstallments(data)) {
                this.pushPayInstallmentsDescriptor(descriptors, pledgeId, data);
            }
            this.pushViewPaymentsDescriptor(descriptors, pledgeId, data);
            this.addResendReceiptDescriptor(descriptors, pledgeId, data);
            return;
        }
        const amount = parseFloat(data.amount) || 0;
        const balanceDue = parseFloat(data.balance_due) || 0;
        if (balanceDue === amount || amount === 0) {
            this.pushPayBillDescriptor(descriptors, pledgeId, data);
            return;
        }
        this.pushViewPaymentsDescriptor(descriptors, pledgeId, data);
        this.pushPayBillDescriptor(descriptors, pledgeId, data);
        this.addResendReceiptDescriptor(descriptors, pledgeId, data);
    },

    pushViewPaymentsDescriptor(descriptors, pledgeId, data) {
        descriptors.push(this.makeActionDescriptor('View Payments', 'primary', {
            action: 'view-recur-payments',
            attrs: this.pledgeRowAttrs(pledgeId, data),
            group: 'payment',
            icon: 'fa-solid fa-dollar-sign',
        }));
    },

    pushActivateRecurringDescriptor(descriptors, pledgeId, data) {
        if (!data.is_cancelled) {
            return;
        }
        if (this.portal === 'manager' && !this.canShowWriteAction('pledges')) {
            return;
        }
        descriptors.push(this.makeActionDescriptor('Activate Schedule', 'primary', {
            action: 'activate-recurring',
            attrs: this.pledgeRowAttrs(pledgeId, data),
            group: 'management',
            icon: 'fa-solid fa-rotate-right',
            writeOption: this.portal === 'manager',
        }));
    },

    isEditableFailedRecurring(data) {
        return !!(data.recurring && data.is_failed && !data.is_cancelled && !data.awaiting_activation);
    },

    canOfferEditRecurring(data) {
        if (data.is_cancelled || data.awaiting_activation) {
            return false;
        }
        if (this.isEditableFailedRecurring(data)) {
            return true;
        }
        if (!data.active_ongoing) {
            return false;
        }
        return data.type === 'Donation' || !data.paid;
    },

    pushEditRecurringDescriptor(descriptors, pledgeId, data) {
        if (!this.canOfferEditRecurring(data)) {
            return;
        }
        descriptors.push(this.makeActionDescriptor('Edit Recurring', 'primary', {
            action: 'edit-ongoing',
            attrs: this.pledgeRowAttrs(pledgeId, data),
            group: 'management',
            icon: 'fa-solid fa-edit',
            writeOption: this.portal === 'manager',
        }));
        descriptors.push(this.makeActionDescriptor('Cancel Recurring', 'danger', {
            action: 'cancel-recurring',
            attrs: this.pledgeRowAttrs(pledgeId, data),
            group: 'management',
            icon: 'fa-solid fa-ban',
            writeOption: this.portal === 'manager',
        }));
    },

    pushPayBillDescriptor(descriptors, pledgeId, data) {
        const label = data.awaiting_activation ? 'Start Schedule' : 'Pay Bill';
        descriptors.push(this.makeActionDescriptor(label, 'primary', {
            action: 'open-pay-bill-modal',
            attrs: this.pledgeRowAttrs(pledgeId, data),
            group: 'payment',
            extraClass: 'action-btn-accent-fill',
            accent: true,
            icon: 'fa-solid fa-credit-card',
            writeOption: this.portal === 'manager',
        }));
    },

    canShowPayInstallments(data) {
        if (data.is_cancelled || data.awaiting_activation) {
            return false;
        }
        if (!(data.active_ongoing || data.recurring)) {
            return false;
        }
        if (data.type === 'Donation') {
            return true;
        }
        return !data.paid;
    },

    pushPayInstallmentsDescriptor(descriptors, pledgeId, data) {
        if (this.portal === 'manager' && !this.canShowWriteAction('pledges')) {
            return;
        }
        descriptors.push(this.makeActionDescriptor('Pay installment(s)', 'primary', {
            action: 'open-pay-installments-modal',
            attrs: this.pledgeRowAttrs(pledgeId, data),
            group: 'payment',
            extraClass: 'action-btn-accent-fill',
            accent: true,
            icon: 'fa-solid fa-layer-group',
            writeOption: this.portal === 'manager',
        }));
    },

    addResendReceiptDescriptor(descriptors, pledgeId, data) {
        descriptors.push(this.makeActionDescriptor('Re-Send Receipt', 'primary', {
            action: 'resend-receipt',
            attrs: {
                ...this.pledgeRowAttrs(pledgeId, data),
                'data-recurring': data.recurring ? 1 : 0,
            },
            group: 'statement',
            icon: 'fa-solid fa-envelope',
        }));
    },

    addViewSentStatementsDescriptor(descriptors, pledgeId, data) {
        if (!data.statement_sent || data.type !== 'Pledge') {
            return;
        }
        descriptors.push(this.makeActionDescriptor('View Sent Statement(s)', 'primary', {
            action: 'view-sent-statements',
            attrs: this.pledgeRowAttrs(pledgeId, data),
            group: 'statement',
            icon: 'fa-solid fa-file-invoice',
        }));
    },

    addPaidPledgeDescriptors(descriptors, pledgeId, data) {
        if (this.canShowPayInstallments(data)) {
            this.pushPayInstallmentsDescriptor(descriptors, pledgeId, data);
        }
        this.addResendReceiptDescriptor(descriptors, pledgeId, data);
        this.pushViewPaymentsDescriptor(descriptors, pledgeId, data);
        const editLabel = data.type === 'Donation' ? 'Edit Donation' : 'Edit Pledge';
        if (this.canShowWriteAction('pledges')) {
            descriptors.push(this.makeActionDescriptor(editLabel, 'primary', {
                action: 'edit-paid-donation',
                attrs: this.pledgeRowAttrs(pledgeId, data),
                group: 'management',
                writeOption: true,
                icon: 'fa-solid fa-edit',
            }));
            if (data.type === 'Donation' && data.table_name === 'campaign_donations') {
                descriptors.push(this.makeActionDescriptor('Apply to Pledge', 'primary', {
                    action: 'apply-donation-to-pledge',
                    attrs: {
                        ...this.pledgeRowAttrs(pledgeId, data),
                        'data-amount': data.amount || 0,
                        'data-user-id': data.user_id || '',
                    },
                    group: 'management',
                    writeOption: true,
                    icon: 'fa-solid fa-file-import',
                }));
            }
        }
        this.addViewSentStatementsDescriptor(descriptors, pledgeId, data);
    },

    addRecurringPledgeDescriptors(descriptors, pledgeId, data) {
        if (this.canOfferEditRecurring(data) && this.canShowWriteAction('pledges')) {
            this.pushEditRecurringDescriptor(descriptors, pledgeId, data);
        }
        if (this.canShowPayInstallments(data)) {
            this.pushPayInstallmentsDescriptor(descriptors, pledgeId, data);
        }
        this.addResendReceiptDescriptor(descriptors, pledgeId, data);
        this.pushViewPaymentsDescriptor(descriptors, pledgeId, data);
    },

    pledgeHasAppliedPayment(data) {
        if (data.awaiting_activation) {
            return false;
        }
        return data.paid === 1 || data.amount > data.balance_due;
    },

    addUnpaidPledgeDescriptors(descriptors, pledgeId, data) {
        if (this.pledgeHasAppliedPayment(data)) {
            this.addResendReceiptDescriptor(descriptors, pledgeId, data);
            this.pushViewPaymentsDescriptor(descriptors, pledgeId, data);
        }
        if (this.canShowWriteAction('pledges')) {
            const editAction = data.recurring && data.awaiting_activation
                ? 'edit-ongoing'
                : 'open-edit-modal';
            descriptors.push(this.makeActionDescriptor('Edit', 'primary', {
                action: editAction,
                attrs: this.pledgeRowAttrs(pledgeId, data),
                group: 'management',
                writeOption: true,
                icon: 'fa-solid fa-edit',
            }));
            if (data.awaiting_activation || data.amount === data.balance_due || data.amount === 0) {
                descriptors.push(this.makeActionDescriptor('Delete', 'danger', {
                    action: 'open-delete-modal',
                    attrs: this.pledgeRowAttrs(pledgeId, data),
                    group: 'management',
                    writeOption: true,
                    icon: 'fa-solid fa-trash',
                }));
            }
            this.pushPayBillDescriptor(descriptors, pledgeId, data);
            const zeroBalance = !data.matanah && (parseFloat(data.amount) === 0 || parseFloat(data.balance_due) === 0);
            if (!data.awaiting_activation) {
                descriptors.push(this.makeActionDescriptor('Send Statement', 'primary', {
                    action: 'open-invoice-modal',
                    attrs: this.pledgeRowAttrs(pledgeId, data),
                    group: 'statement',
                    writeOption: true,
                    disabled: zeroBalance,
                    tooltip: zeroBalance ? DISABLED_TOOLTIPS.sendStatement : undefined,
                    icon: 'fa-solid fa-paper-plane',
                }));
            }
        }
        if (!data.awaiting_activation && !data.paid && !isManagerKioskNoPrint()) {
            descriptors.push(this.makeActionDescriptor('Print Statement', 'primary', {
                action: 'open-print-statement-modal',
                attrs: {
                    ...this.pledgeRowAttrs(pledgeId, data),
                    'data-user-id': data.user_id || '',
                },
                group: 'statement',
                icon: 'fa-solid fa-print',
            }));
        }
        if (!data.awaiting_activation) {
            this.addViewSentStatementsDescriptor(descriptors, pledgeId, data);
        }
    },

    addCommonDescriptors(descriptors, pledgeId, data) {
        const isGuest = (data.added_by || '') === 'Guest';
        const associateAttrs = {
            ...this.pledgeRowAttrs(pledgeId, data),
            'data-user-name': data.user_name || '',
            'data-amount': data.amount || 0,
            'data-description': data.description || '',
        };
        if (isGuest) {
            if (this.canShowWriteAction('pledges')) {
                descriptors.push(this.makeActionDescriptor('Associate with Member', 'primary', {
                    action: 'open-associate-modal',
                    attrs: {
                        ...associateAttrs,
                        'data-associate-mode': 'associate',
                    },
                    group: 'member',
                    writeOption: true,
                    icon: 'fa-solid fa-link',
                }));
            }
        } else {
            if (this.canShowWriteAction('pledges')) {
                descriptors.push(this.makeActionDescriptor('Associate w/ Different Member', 'primary', {
                    action: 'open-associate-modal',
                    attrs: {
                        ...associateAttrs,
                        'data-associate-mode': 're-associate',
                    },
                    group: 'member',
                    writeOption: true,
                    icon: 'fa-solid fa-link',
                }));
            }
            const alreadyViewingMemberPledges = this.sourcePage === 'pledges' && isFilteredToMember(data.user_id);
            descriptors.push(this.makeActionDescriptor('View All Pledges', 'primary', {
                action: 'view-all-member-pledges',
                attrs: {
                    'data-user-id': data.user_id,
                },
                group: 'member',
                icon: 'fa-solid fa-list',
                disabled: alreadyViewingMemberPledges,
                tooltip: alreadyViewingMemberPledges ? DISABLED_TOOLTIPS.alreadyViewingMemberPledges : undefined,
            }));
        }
        const hasPaidActivity = this.pledgeHasAppliedPayment(data);
        if (data.allow_delete && hasPaidActivity && this.canShowWriteAction('pledges')) {
            descriptors.push(this.makeActionDescriptor('Delete Paid', 'danger', {
                action: 'open-delete-modal',
                attrs: {
                    ...this.pledgeRowAttrs(pledgeId, data),
                    'data-delete-paid': '1',
                },
                group: 'management',
                writeOption: true,
                icon: 'fa-solid fa-trash',
            }));
        }
    },

    buildMemberActionDescriptors(memberId, data) {
        const descriptors = [];
        const hasPledgeIds = !!data.pledge_ids;
        const hasContact = memberHasEmailOrPhone(data);
        const memberArchived = !!data.congregation_archive;
        const sendDisabled = !hasPledgeIds || !hasContact;
        const sendTooltip = !hasContact ? DISABLED_TOOLTIPS.sendStatementNoContact :
            !hasPledgeIds ? DISABLED_TOOLTIPS.sendStatementNoPledges : undefined;
        const printDisabled = !hasPledgeIds;
        const printTooltip = !hasPledgeIds ? DISABLED_TOOLTIPS.printStatementNoPledges : undefined;

        if (this.canShowWriteAction('members')) {
            descriptors.push(this.makeActionDescriptor('Send Statement', 'primary', {
                onClick: () => {
                    this.callCallback('openInvoiceModal', data.pledge_ids);
                },
                expandAction: 'send-statement',
                attrs: {
                    'data-pledge-ids': data.pledge_ids || '',
                    'data-member-id': memberId,
                },
                group: 'statement',
                writeOption: true,
                disabled: sendDisabled,
                tooltip: sendTooltip,
                icon: 'fa-solid fa-envelope',
            }));

            if (!isManagerKioskNoPrint()) {
                descriptors.push(this.makeActionDescriptor('Print Statement', 'primary', {
                    onClick: () => {
                        this.callCallback('openPrintStatementModal', data.pledge_ids, memberId);
                    },
                    expandAction: 'print-statement',
                    attrs: {
                        'data-pledge-ids': data.pledge_ids || '',
                        'data-member-id': memberId,
                    },
                    group: 'statement',
                    writeOption: true,
                    disabled: printDisabled,
                    tooltip: printTooltip,
                    icon: 'fa-solid fa-print',
                }));
            }
        }

        const alreadyViewingMemberPledges = this.sourcePage === 'pledges' && isFilteredToMember(memberId);
        descriptors.push(this.makeActionDescriptor('View all pledges', 'primary', {
            onClick: () => {
                this.callCallback('viewAllMemberPledge', memberId, this.sourcePage);
            },
            expandAction: 'view-all-pledges',
            attrs: {
                'data-member-id': memberId,
            },
            group: 'pledges',
            icon: 'fa-solid fa-list',
            disabled: alreadyViewingMemberPledges,
            tooltip: alreadyViewingMemberPledges ? DISABLED_TOOLTIPS.alreadyViewingMemberPledges : undefined,
        }));

        descriptors.push(this.makeActionDescriptor('Full profile', 'primary', {
            profileCta: true,
            memberId,
            group: 'status',
        }));

        if (!memberArchived) {
            return descriptors;
        }
        return descriptors.map(function (descriptor) {
            if (descriptor.profileCta) {
                return descriptor;
            }
            return lockArchivedMemberDescriptor(descriptor);
        });
    },

    buildMemberActionButtons(memberId, data) {
        return this.renderActionButtons(
            this.buildMemberActionDescriptors(memberId, data),
            this.renderDesktopActionButton
        );
    },

    buildMemberMobileActionDescriptors(memberId, data) {
        const memberArchived = !!data.congregation_archive;
        const descriptors = this.buildMemberActionDescriptors(memberId, data).map((descriptor) => {
            if (descriptor.profileCta || descriptor.expandAction) {
                return { ...descriptor, onClick: null };
            }
            return descriptor;
        });

        const extraDescriptors = [
            this.makeActionDescriptor('View', 'primary', {
                expandAction: 'view-member',
                attrs: { 'data-member-id': memberId },
                group: 'management',
                icon: 'fa-solid fa-eye',
            }),
        ];

        if (this.canShowWriteAction('members')) {
            extraDescriptors.push(this.makeActionDescriptor('Edit', 'primary', {
                expandAction: 'edit-member',
                attrs: { 'data-member-id': memberId },
                group: 'management',
                writeOption: true,
                icon: 'fa-solid fa-edit',
            }));
        }

        extraDescriptors.push(this.makeActionDescriptor('View Mi Sheberach card', 'primary', {
            expandAction: 'view-mi-sheberach-card',
            attrs: { 'data-member-id': memberId },
            group: 'management',
            icon: 'fa-solid fa-eye',
        }));

        if (this.canShowWriteAction('members')) {
            extraDescriptors.push(this.makeActionDescriptor('Edit Mi Sheberach card', 'primary', {
                expandAction: 'open-mi-sheberach-card',
                attrs: { 'data-member-id': memberId },
                group: 'management',
                writeOption: true,
                icon: 'fa-solid fa-edit',
            }));
        }

        extraDescriptors.push(this.makeActionDescriptor('Download Mi Sheberach card', 'primary', {
            expandAction: 'download-mi-sheberach-card',
            attrs: { 'data-member-id': memberId },
            group: 'management',
            icon: 'fa-solid fa-download',
            disabled: isKioskDownloadsDisabled(),
            tooltip: isKioskDownloadsDisabled() ? kioskDownloadsDisabledMessage() : undefined,
        }));

        extraDescriptors.push(this.makeActionDescriptor('View Relationships', 'primary', {
            expandAction: 'view-relationships',
            attrs: { 'data-member-id': memberId },
            group: 'member',
            icon: 'fa-solid fa-link',
        }));

        const combined = descriptors.concat(
            memberArchived ? extraDescriptors.map(lockArchivedMemberDescriptor) : extraDescriptors
        );
        return combined;
    },

    hydrateManagerPledgeMobileActions(grid) {
        if (!(grid instanceof HTMLElement)) {
            return;
        }
        grid.querySelectorAll('.data-grid-item[data-pledge-id]').forEach((item) => {
            const $item = $(item);
            const $row = $item.find('.clickable-row').first();
            if (!$row.length) {
                return;
            }
            const $panel = $item.find('.data-grid-mobile-actions-buttons').first();
            if (!$panel.length) {
                return;
            }
            const pledgeId = $row.data('pledge-id');
            const tableName = $row.data('table-name');
            if (pledgeId == null || pledgeId === '' || !tableName) {
                return;
            }
            const pledgeData = this.resolvePledgeForActions(pledgeId, tableName, $row);
            if (!pledgeData) {
                return;
            }
            this.fillMobileActionsPanel($panel, this.buildActionButtonDescriptors(pledgeId, pledgeData));
        });
    },

    hydrateManagerMemberMobileActions(grid) {
        if (!(grid instanceof HTMLElement)) {
            return;
        }
        grid.querySelectorAll('.data-grid-item[data-member-id]').forEach((item) => {
            const $item = $(item);
            const $row = $item.find('.clickable-row').first();
            if (!$row.length) {
                return;
            }
            const memberId = $row.data('member-id');
            if (memberId == null || memberId === '') {
                return;
            }
            const memberData = this.getMemberDataFromRow($row);
            const $panel = $item.find('.data-grid-mobile-actions-buttons').first();
            if (!$panel.length) {
                return;
            }
            this.fillMobileActionsPanel(
                $panel,
                this.buildMemberMobileActionDescriptors(memberId, memberData)
            );
        });
    },

    hydrateMobileRowActions() {
        if (!this.isMobileViewport()) {
            return;
        }
        const gridId = String(this.gridId || '').trim();
        if (!gridId) {
            return;
        }
        const grid = document.getElementById(gridId);
        if (!grid) {
            return;
        }
        if (gridId === 'pledges-grid' && this.portal === 'manager') {
            this.hydrateManagerPledgeMobileActions(grid);
            return;
        }
        if (gridId === 'members-grid') {
            this.hydrateManagerMemberMobileActions(grid);
        }
    },
};
